import { AxiosResponse } from "axios";
import apiClient from "../../../../shared/infrastructure/api/client";
import {
    BillingPeriod,
    CancellationReason,
    CouponDTO,
    PlanTier,
    PlusSubscriptionStateDTO,
    QuoteDTO,
} from "../../domain/types";

/**
 * The account's own subscription. No endpoint takes a subscription id: the
 * server resolves it from the authenticated caller, so there is nothing to
 * pass and nothing to point at somebody else's.
 */
export const PlusSubscriptionAPI = {
    state: (): Promise<AxiosResponse<PlusSubscriptionStateDTO>> =>
        apiClient.get("/plus-subscription"),

    /**
     * Returns the Stripe Checkout URL for the caller to navigate to.
     *
     * Not followed here: this is an authenticated XHR, and Checkout has to be
     * a top-level navigation for the therapist to see and use it.
     */
    checkout: (
        period: BillingPeriod,
        coupon?: string,
        modules: string[] = [],
        tier: PlanTier = "plus"
    ): Promise<AxiosResponse<{ url: string }>> =>
        apiClient.post("/plus-subscription/checkout", { period, coupon, modules, tier }),

    /**
     * What the purchase costs with these modules ticked.
     *
     * Asked of the server on every change rather than added up here: the
     * annual discount and the per-period arithmetic live in one place, and a
     * second copy of them in the browser would start disagreeing with the
     * invoice.
     */
    quote: (
        period: BillingPeriod,
        modules: string[] = [],
        coupon?: string,
        tier: PlanTier = "plus"
    ): Promise<AxiosResponse<QuoteDTO>> =>
        apiClient.post("/plus-subscription/quote", { period, modules, coupon, tier }),

    /**
     * Move between Plus and Max. Up at once, the difference charged now; down
     * at the renewal, with Max kept until the period paid for ends.
     */
    changeTier: (tier: PlanTier): Promise<AxiosResponse<PlusSubscriptionStateDTO>> =>
        apiClient.post("/plus-subscription/tier", { tier }),

    /** What a discount code is worth, before anybody pays. */
    coupon: (code: string): Promise<AxiosResponse<CouponDTO>> =>
        apiClient.post("/plus-subscription/coupon", { code }),

    /**
     * Stop renewing. Plus stays until the period already paid for ends.
     * The reason and comment are optional and go to the provider's churn
     * reports; leaving never waits on them.
     */
    cancel: (
        reason?: CancellationReason,
        comment?: string
    ): Promise<AxiosResponse<PlusSubscriptionStateDTO>> =>
        apiClient.post("/plus-subscription/cancel", {
            reason: reason ?? null,
            comment: comment?.trim() ? comment.trim() : null,
        }),

    /** Undo a pending cancellation, on the same subscription. No new charge. */
    resume: (): Promise<AxiosResponse<PlusSubscriptionStateDTO>> =>
        apiClient.post("/plus-subscription/resume"),

    /** With `updatePaymentMethod`, the portal opens straight on changing the card. */
    billingPortal: (updatePaymentMethod = false): Promise<AxiosResponse<{ url: string }>> =>
        apiClient.post("/plus-subscription/billing-portal", {
            update_payment_method: updatePaymentMethod,
        }),
};
